import React from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Row, Col } from "react-bootstrap";
import Header from "../Includes/Header";
import Footer from "../Includes/Footer";
import "../../App.css";
import { useGeoConfig } from '../../context/GeoConfigContext';
import { getCountryDisplayName } from '../../geoConfig';

const GlobalAvailabilityPage: React.FC = () => {
  const { t } = useTranslation();
  const { config, isLoading } = useGeoConfig();

  // Helper to render a section of countries
  const renderCountryGrid = (countries: string[], icon: string, emptyMessage: string) => {
    if (countries.length === 0) {
      return <p className="text-secondary">{emptyMessage}</p>;
    }
    return (
      <Row>
        {countries.map((code) => (
          <Col xs={12} sm={6} md={4} key={code} className="mb-3">
            <div className="p-3 border rounded d-flex align-items-center gap-2 bg-light text-dark">
              <span style={{ fontSize: '1.2rem' }}>{icon}</span>
              <span className="fw-medium">{getCountryDisplayName(code)}</span>
            </div>
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <>
      <Header />
      <Container className="py-5 mt-4" style={{ maxWidth: 820 }}>
        <h1 className="fw-bold mb-1">
          {t('geo.global_availability_title', { defaultValue: 'Global Service Availability' })}
        </h1>
        
        {isLoading ? (
          <div className="text-secondary my-5">Loading global availability data...</div>
        ) : (
          <>
            {/* Available Countries */}
            <div className="mt-5 mb-4">
              <h4 className="fw-semibold mb-2 text-success">Available in Countries</h4>
              <p className="text-secondary small mb-3" style={{ lineHeight: 1.8, textAlign: 'justify' }}>
                {t('geo.available_countries_desc', {
                  defaultValue: 'ARM Financial Services Pty Ltd (ABN 35 659 595 570) is registered with AUSTRAC (Reference Number 75630) to provide remittance services in Australia. Users residing in the region below have access to our full onboarding, identity verification (KYC), and transaction capabilities.'
                })}
              </p>
              {renderCountryGrid(config.availableCountries, '✅', 'No countries listed at this time.')}
            </div>

            {/* Coming Soon */}
            <div className="mt-5 mb-4">
              <h4 className="fw-semibold mb-2 text-primary">Coming Soon</h4>
              <p className="text-secondary small mb-3" style={{ lineHeight: 1.8, textAlign: 'justify' }}>
                {t('geo.coming_soon_desc', {
                  defaultValue: "We're working towards extending our services to the jurisdictions below. You can currently create an account and join our waitlist, but full identity verification (KYC) and transaction capabilities are not yet available there. We'll notify you as soon as operations begin."
                })}
              </p>
              {renderCountryGrid(config.comingSoonCountries, '⏳', 'No upcoming regions announced yet.')}
            </div>



            {/* Restricted Regions */}
            <div className="mt-5 mb-4">
              <h4 className="fw-semibold mb-2 text-danger">Restricted Regions</h4>
              <p className="text-secondary small mb-3" style={{ lineHeight: 1.8, textAlign: 'justify' }}>
                {t('geo.restricted_regions_desc', {
                  defaultValue: 'In line with our obligations under the AML/CTF Act 2006 (Cth), Australian sanctions law, and international sanctions regimes, we are unable to offer our services to individuals or entities residing in or operating from certain jurisdictions. These restrictions help mitigate risks associated with geopolitical instability, active conflict, state-sponsored terrorism, and jurisdictions with significant deficiencies in their AML/CTF frameworks. Users attempting to onboard from the following restricted regions will be denied access to our platform.'
                })}
              </p>
              {renderCountryGrid(config.blockedCountries, '🚫', 'No restricted regions at this time.')}
            </div>
          </>
        )}

        <div className="mt-5 pt-3 border-top text-secondary small">
          {t('geo.restricted_regions_footer', { defaultValue: 'If you have questions about these restrictions, please contact us at support@armf.com.' })}
        </div>
      </Container>
      <Footer full={true} />
    </>
  );
};

export default GlobalAvailabilityPage;
